import { Controller } from "@hotwired/stimulus"
import { loadedQuill } from "../../src/shared/quill/load_quill"

// Activation email: while the form changes, keeps the invoice paragraph and the
// invoice attachment in line with Profiles::Practitioner#entry_fee_due? (fee not
// paid, not exempted, training switched off). The invoice paragraph goes right
// after the presentation one (anchor) while the fee is due, and goes away when
// it is not. A message whose anchor was edited is left alone.
export default class extends Controller {
  static targets = ["invoiceAttachment"]
  static values = {
    anchor: String,
    invoice: String,
    due: Boolean,
    paid: Boolean,
    exempted: Boolean,
    trainingField: String,
    entryFeeName: String
  }

  connect () {
    this.form = this.element.closest("form") || this.element
    this.sync = this.sync.bind(this)
    this.form.addEventListener("change", this.sync)
    // The wysiwyg editor below loads Quill on demand: sync once it is ready, or now if it already is.
    this.form.addEventListener("wysiwyg:ready", this.sync)
    this.timeout = setTimeout(this.sync, 0)
  }

  disconnect () {
    clearTimeout(this.timeout)
    this.form.removeEventListener("change", this.sync)
    this.form.removeEventListener("wysiwyg:ready", this.sync)
  }

  sync () {
    const due = this.due
    if (this.hasInvoiceAttachmentTarget) this.invoiceAttachmentTarget.hidden = !due

    if (due) {
      this.insertInvoiceParagraph()
    } else {
      this.removeInvoiceParagraph()
    }
  }

  get due () {
    const training = document.getElementById(this.trainingFieldValue)
    if (!training) return this.dueValue
    if (this.paidValue) return false

    const entryFee = this.form.querySelector(`input[name="${this.entryFeeNameValue}"]:checked`)
    const exempted = entryFee ? entryFee.value === "exempted" : this.exemptedValue

    return !training.checked && !exempted
  }

  insertInvoiceParagraph () {
    const quill = this.quill
    if (!quill || this.findParagraph(quill, this.invoiceValue)) return

    const anchor = this.findParagraph(quill, this.anchorValue)
    if (!anchor) return

    const { Quill, Delta } = loadedQuill()
    const blot = Quill.find(anchor)
    const index = quill.getIndex(blot) + blot.length()
    const converted = quill.clipboard.convert({ html: this.invoiceValue })
    // The inserted paragraph needs its own line break.
    const endsWithNewline = converted.ops.at(-1)?.insert?.endsWith?.("\n")
    const content = endsWithNewline ? converted : converted.insert("\n")

    quill.updateContents(new Delta().retain(index).concat(content), "api")
  }

  removeInvoiceParagraph () {
    const quill = this.quill
    if (!quill) return

    const node = this.findParagraph(quill, this.invoiceValue)
    if (!node) return

    const { Quill, Delta } = loadedQuill()
    const blot = Quill.find(node)

    quill.updateContents(new Delta().retain(quill.getIndex(blot)).delete(blot.length()), "api")
  }

  findParagraph (quill, html) {
    const text = textOf(html)
    return Array.from(quill.root.children).find((element) => element.textContent.trim() === text)
  }

  get quill () {
    const element = this.element.querySelector("[data-controller~='wysiwyg']")
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(element, "wysiwyg")?.quill
  }
}

function textOf (html) {
  const element = document.createElement("div")
  element.innerHTML = html
  return element.textContent.trim()
}
